import { MoneyScaled } from '@quickbit/qb-design-system';
import {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useSessionStorage } from 'react-use';

const SESSION_STORAGE_KEY = 'merchant_bo_send_data';

type SendData = {
  walletBalance?: MoneyScaled;
  walletBalanceReference?: MoneyScaled;
  merchantId?: string;
  previewId?: string;
};

type SendProviderValue =
  | (SendData & {
      setSendData: (sendData: SendData) => void;
    })
  | {
      setSendData: (sendData: SendData) => void;
    };

const SendContext = createContext<SendProviderValue>({
  setSendData: () => undefined,
});

export const useSetSendData = (): ((data: SendData) => void) =>
  useContext(SendContext).setSendData;

export const useSendData: () => SendData = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setSendData, ...rest } = useContext(SendContext);
  return rest;
};

export const SendProvider = ({ children }: { children: React.JSX.Element }) => {
  const [sessionSendData, setSessionSendData] = useSessionStorage<SendData>(
    SESSION_STORAGE_KEY,
    {}
  );
  const [sendData, setSendData] = useState<SendData>(sessionSendData);

  const updateSendData = useCallback((data: SendData) => {
    setSendData((prevState) => ({ ...prevState, ...data }));
  }, []);

  useEffect(() => {
    setSessionSendData(sendData);
  }, [sendData, setSessionSendData]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    };
  }, []);

  const value = useMemo(
    () => ({ ...sendData, setSendData: updateSendData }),
    [sendData, updateSendData]
  );

  return <SendContext.Provider value={value}>{children}</SendContext.Provider>;
};
